import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Filter, Search } from 'lucide-react';
import { blogService } from '../services';
import { buildCategoryTree } from '../utils/blog';
import { LoadingSpinner } from '../components/ui/Loading';
import PageContainer from '../components/PageContainer';
import EmptyState from '../components/EmptyState';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    blogService.getCategories()
      .then((res) => setCategories(res.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await blogService.getPosts({
        page,
        limit: 12,
        categoryId: categoryId || undefined,
        search: search.trim() || undefined,
      });
      setPosts(res.posts || []);
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (e) {
      console.error('Erro ao carregar posts:', e);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [page, categoryId, search]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const tree = buildCategoryTree(categories);

  return (
    <PageContainer title="Blog" description="Novidades e guias do clã">
      {/* Filters */}
      <Card variant="glass" className="mb-8">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Filter className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Filtros</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              icon={Search}
              placeholder="Buscar por título..."
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              variant="glass"
            />

            <select
              value={categoryId}
              onChange={(e) => { setPage(1); setCategoryId(e.target.value); }}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todas as categorias</option>
              {tree.map((parent) => (
                <optgroup key={parent.id} label={parent.name}>
                  <option value={parent.id} className="bg-slate-800">{parent.name}</option>
                  {parent.children.map((child) => (
                    <option key={child.id} value={child.id} className="bg-slate-800">&nbsp;&nbsp;{child.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : posts.length === 0 ? (
        <EmptyState
          title="Nenhum post encontrado"
          description="Ainda não há posts publicados ou nenhum corresponde aos filtros aplicados."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link key={post.id} to={`/app/blog/${post.id}`} className="block">
              <Card variant="glass" className="group hover:scale-[1.02] transition-all duration-300 cursor-pointer h-full">
                <div className="p-6">
                  <div className="text-xs text-purple-300 mb-1">{post.category?.name || 'Sem categoria'}</div>
                  <h2 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors duration-200">
                    {post.title}
                  </h2>
                  {post.status === 'DRAFT' && (
                    <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-300">Rascunho</span>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-8 gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Anterior</Button>
          <span className="px-4 py-2 text-sm text-gray-400">Página {page} de {totalPages}</span>
          <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Próxima →</Button>
        </div>
      )}
    </PageContainer>
  );
};

export default Blog;
